import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AdministrativeRuleAddendum,
  CollectionResponse,
  PaginationModel,
  PendingAdministrativeRuleModel,
  StudentAdministrativeRuleModel,
} from '@core/models';
import { Api } from '@core/services/api.state';
import { ApiService } from '@core/services/api/api.service';
import { LayoutState } from '@core/state/shared';
import {
  DialogCloseResult,
  DialogService,
} from '@progress/kendo-angular-dialog';
import { environment } from '@rcsa-frontend/environments/environment';
import { AdministrativeRulesAddendumComponent } from '@widgets/administrative-rules/components';
import { Observable, map, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AdministrativeRulesService extends Api<StudentAdministrativeRuleModel> {
  override endpoint: string;

  constructor(
    protected override readonly apiService: ApiService<StudentAdministrativeRuleModel>,
    private http: HttpClient,
    private dialogService: DialogService,
    private layoutState: LayoutState
  ) {
    super(apiService);
    this.endpoint = `${environment.apiUrl}/v1/account/administrative-rules`;
  }

  public getAdministrativeRules(
    params: PaginationModel
  ): Observable<CollectionResponse<StudentAdministrativeRuleModel>> {
    const url = `${this.endpoint}/${`?${new URLSearchParams(
      params
    ).toString()}`}`;
    return this.http
      .get<CollectionResponse<StudentAdministrativeRuleModel>>(url)
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  public getAdministrativeRulesByStudentCycle(
    ids: string
  ): Observable<StudentAdministrativeRuleModel[]> {
    const url = `${this.endpoint}/indexByCycle${`?${new URLSearchParams({
      ids,
    }).toString()}`}`;

    return this.http
      .get<CollectionResponse<StudentAdministrativeRuleModel>>(url)
      .pipe(
        map((response) => {
          return response.data.items;
        })
      );
  }

  public getPendingAdministrativeRules(): Observable<
    PendingAdministrativeRuleModel[]
  > {
    const url = `${this.endpoint}/pending`;
    return this.http
      .get<CollectionResponse<PendingAdministrativeRuleModel>>(url)
      .pipe(
        map((response) => {
          return response.data.items;
        })
      );
  }

  public getDocumentsAdministrativeRules(
    schoolId: string,
    document: string
  ): Observable<Blob> {
    const url = `${this.endpoint}/preview/${document}/${schoolId}`;
    return this.http.get(url, { responseType: 'blob' }).pipe(
      map((response) => {
        return response;
      })
    );
  }

  public acceptPendingAddendum(
    administrativeRule: PendingAdministrativeRuleModel,
    addendums: AdministrativeRuleAddendum[],
    fromGuard = false
  ): Observable<boolean> {
    const dialogRef = this.dialogService.open({
      title: fromGuard
        ? 'Para Continuar el Pago Debe Aceptar la Adenda'
        : 'Aceptar Adenda',
      content: AdministrativeRulesAddendumComponent,
      width: this.layoutState.isMobile ? '95%' : '40%',
      height: 'auto',
      maxHeight: '90%',
      cssClass: 'confirm-dialog',
    });
    const dialogComponent = dialogRef.content
      .instance as AdministrativeRulesAddendumComponent;
    dialogComponent.administrativeRule = administrativeRule;
    dialogComponent.addendums = addendums;
    dialogComponent.fromGuard = fromGuard;
    return dialogRef.result.pipe(
      switchMap((result: any) => {
        if (result instanceof DialogCloseResult) {
          return of(false);
        }
        if (result.text === 'confirm') {
          return of(true);
        }
        return of(false);
      })
    );
  }

  public acceptAddendum(
    data: PendingAdministrativeRuleModel
  ): Observable<boolean> {
    const endpoint = `${environment.apiUrl}/v1/account/administrative-rules/chargeback-addendum`;
    return this.http.post(endpoint, data).pipe(map(() => true));
  }
}
